import { useState } from 'react';
import { useStore, selectCreature } from '@/infra/store';
import { getBreedingService, type BreedOutcome } from '@/services/breeding';
import { useT } from '@/infra/i18n';
import { COLORS, FONT, SPACING } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';

const ASSET_ROOT = '/assets/generated/house';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function BreedingModal({ open, onClose }: Props) {
  const t = useT();
  const c = useStore(selectCreature);
  const [busy, setBusy] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<(BreedOutcome & { msg: string }) | null>(null);

  if (!open || !c) return null;
  const svc = getBreedingService();
  const canBreed = svc.canBreed();
  const partners = canBreed ? svc.getEligiblePartners() : [];

  const handleBreed = async (): Promise<void> => {
    setBusy(true);
    const r = await svc.breed(selectedPartnerId ?? undefined);
    setBusy(false);
    if (r.ok) {
      setOutcome({ ...r, msg: t('breeding.success') });
    } else {
      setOutcome({ ok: false, msg: t('breeding.failure', { reason: r.reason ?? '?' }) });
    }
  };

  return (
    <div
      role="dialog"
      data-testid="breeding-modal"
      style={overlayStyle}
      onClick={() => {
        setSelectedPartnerId(null);
        setOutcome(null);
        onClose();
      }}
    >
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={title}>
          <img src={`${ASSET_ROOT}/icon-breeding.png`} alt="" draggable={false} style={titleIcon} />
          {t('breeding.title')}
        </h2>
        {!outcome?.ok && <p style={body}>
          {canBreed
            ? t('breeding.canBreed', { name: c.name })
            : t('breeding.cannotBreed', { name: c.name })}
        </p>}

        {canBreed && partners.length === 0 && (
          <p style={body}>{t('breeding.noPartners', { name: c.name })}</p>
        )}

        {canBreed && partners.length > 0 && (
          <div style={{ marginBottom: SPACING.md }}>
            <p style={body}>{t('breeding.choosePartner', { name: c.name })}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.xs }}>
              {partners.map((p) => (
                <label key={p.id} style={partnerRow}>
                  <input
                    type="radio"
                    name="partner"
                    checked={selectedPartnerId === p.id}
                    onChange={() => setSelectedPartnerId(p.id)}
                  />
                  {p.name}
                </label>
              ))}
              <label style={partnerRow}>
                <input
                  type="radio"
                  name="partner"
                  checked={selectedPartnerId === null}
                  onChange={() => setSelectedPartnerId(null)}
                />
                {t('breeding.partnerRandom')}
              </label>
            </div>
          </div>
        )}

        {outcome && (
          <p
            data-testid="breeding-outcome"
            style={{ color: outcome.ok ? COLORS.ok : COLORS.danger, fontSize: FONT.sizeSm, margin: `${SPACING.sm}px 0` }}
          >
            {outcome.msg}
          </p>
        )}
        {outcome?.ok && outcome.inheritance && (
          <div data-testid="inheritance-report" style={inheritanceCard}>
            <h3 style={inheritanceTitle}>{t('breeding.inheritance.title', { name: outcome.childName ?? '' })}</h3>
            <p style={inheritanceParents}>{t('breeding.inheritance.parents', { a: outcome.parentNames?.[0] ?? '', b: outcome.parentNames?.[1] ?? '' })}</p>
            <dl style={inheritanceGrid}>
              {Object.entries(outcome.inheritance).map(([locus, origin]) => (
                <div key={locus} style={inheritanceRow}>
                  <dt style={inheritanceLocus}>{t(`breeding.inheritance.locus.${locus}`)}</dt>
                  <dd style={inheritanceOrigin}>{t(`breeding.inheritance.origin.${origin}`, { a: outcome.parentNames?.[0] ?? '', b: outcome.parentNames?.[1] ?? '' })}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        <div style={{ display: 'flex', gap: SPACING.sm, justifyContent: 'flex-end' }}>
          {outcome?.ok ? (
            <button data-testid="breeding-result-close" onClick={() => { setOutcome(null); setSelectedPartnerId(null); onClose(); }} style={primaryBtn}>
              {t('breeding.inheritance.close')}
            </button>
          ) : <>
          <button
            data-testid="btn-breeding-cancel"
            onClick={() => {
              setSelectedPartnerId(null);
              setOutcome(null);
              onClose();
            }}
            disabled={busy}
            style={secondaryBtn}
          >
            {t('breeding.cancel')}
          </button>
          <button
            data-testid="btn-breeding-confirm"
            onClick={() => void handleBreed()}
            disabled={!canBreed || busy}
            style={canBreed ? primaryBtn : disabledBtn}
          >
            {busy ? '...' : t('breeding.confirm')}
          </button>
          </>}
        </div>
      </div>
    </div>
  );
}

const title: React.CSSProperties = {
  margin: `0 0 ${SPACING.md}px 0`,
  color: COLORS.accent,
  fontSize: FONT.sizeXl,
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.xs,
};
const titleIcon: React.CSSProperties = {
  width: 36,
  height: 36,
  objectFit: 'contain',
  imageRendering: 'auto',
};
const body: React.CSSProperties = {
  margin: `0 0 ${SPACING.md}px 0`,
  color: COLORS.textDim,
  fontSize: FONT.sizeSm,
  lineHeight: 1.6,
};
const partnerRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.xs,
  color: COLORS.text,
  fontSize: FONT.sizeSm,
  cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  background: COLORS.ok,
  color: COLORS.bg,
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  fontWeight: 600,
};
const disabledBtn: React.CSSProperties = { ...primaryBtn, background: '#555', cursor: 'not-allowed' };
const secondaryBtn: React.CSSProperties = {
  background: 'transparent',
  color: COLORS.textDim,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
};
const inheritanceCard: React.CSSProperties = { margin: '12px 0', padding: 12, background: '#101222', border: `1px solid ${COLORS.border}` };
const inheritanceTitle: React.CSSProperties = { margin: '0 0 4px', color: COLORS.accent, fontSize: FONT.sizeMd };
const inheritanceParents: React.CSSProperties = { margin: '0 0 10px', color: COLORS.textDim, fontSize: FONT.sizeSm };
const inheritanceGrid: React.CSSProperties = { display: 'grid', gap: 5, margin: 0 };
const inheritanceRow: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(90px, .8fr) minmax(130px, 1.2fr)', gap: 8, padding: '5px 7px', background: '#1b1d32' };
const inheritanceLocus: React.CSSProperties = { color: '#8ee6d0', fontSize: 11, fontWeight: 700 };
const inheritanceOrigin: React.CSSProperties = { margin: 0, color: COLORS.text, fontSize: 11 };
